import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, FormEvent, MouseEvent, useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import {
  FormLayout,
  Input,
  Label,
  Larger,
  RadioCircle,
  RadioInput,
  RadiosLabel,
  Textarea,
} from '@/components/Forms';
import { Dropdown, Option } from '@/components/Forms/Dropdown';
import Note from '@/components/Forms/Note';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { ChannelResource, ServerResponse } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors, ucFirst } from '@/utils/helpers';

import ChannelCreationGuide from './ChannelCreationGuide';
import ChannelTagsField from './ChannelTagsField';

const ReadMore = styled.span`
  cursor: pointer;
  display: inline-block;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.purpleLighter};
  font-weight: 700;
  border-bottom: 1px solid ${({ theme }) => theme.colors.purpleLighter};
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.purple};
    border-color: ${({ theme }) => theme.colors.purple};
  }
`;

const RadioBox = styled(Larger)`
  display: flex;
  gap: 30px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  height: 46px;
  align-items: center;
  border-radius: 4px;
  padding: 0 16px;
`;

const networks = [
  {
    value: 'facebook',
    label: 'Facebook',
  },
  {
    value: 'youtube',
    label: 'Youtube',
  },
];

export type FormDataT = {
  link: string;
  tags: string[];
  network: Option | null;
  notes: string;
  id: string;
  dedicatedForTesting: boolean;
};

interface Props {
  onClose: () => void;
  competitor: 0 | 1;
  channel?: ChannelResource;
  activeMonth: string;
}

const ChannelFormModal: FC<Props> = ({
  onClose,
  competitor,
  channel,
  activeMonth,
}) => {
  const { teamId } = useParams();

  const [showCreationGuide, setShowCreationGuide] = useState(false);

  const [activeChannelInput, setActiveChannelInput] = useState<'link' | 'id'>(
    'link',
  );

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      id: '',
      link: channel?.url || '',
      tags: channel?.tags?.map((tag) => tag.title) ?? [],
      network: channel?.network
        ? { value: channel.network, label: ucFirst(channel.network) }
        : null,
      notes: channel?.notes || '',
      dedicatedForTesting: false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(
      async ({ link, notes, tags, id, dedicatedForTesting, ...formData }) => {
        try {
          const data = {
            link,
            notes,
            tags,
            id,
            network: formData.network?.value ?? null,
            is_competitor: competitor,
            is_scrapable: !dedicatedForTesting,
          };

          if (channel) {
            // Update existing resource
            const updatedChannel = await api.put<
              { tags: string[] | null; notes: string; is_scrapable: boolean },
              ChannelResource
            >(`/v1/teams/${teamId}/channels/${channel.id}`, {
              tags,
              notes,
              is_scrapable: !dedicatedForTesting,
            });
            queryClient.setQueryData(
              [
                'team_channels',
                {
                  teamId,
                  competitors: updatedChannel.is_competitor ? 1 : 0,
                  month: activeMonth,
                },
              ],
              (channels: ChannelResource[] | undefined) =>
                channels!.map((channel) => {
                  if (channel.id === updatedChannel.id) return updatedChannel;
                  return channel;
                }),
            );
          } else {
            // Create new resource
            const { error, data: newChannel } = await api.post<
              typeof data,
              ServerResponse<ChannelResource>
            >(`/v1/teams/${teamId}/channels`, data);
            if (error) {
              return alerts.error(error);
            }
            queryClient.setQueryData(
              [
                'team_channels',
                { teamId, competitors: competitor ? 1 : 0, month: activeMonth },
              ],
              (channels: ChannelResource[] | undefined) => [
                ...(channels || []),
                newChannel!,
              ],
            );
          }

          onClose();
          alerts.success(
            `${competitor ? 'Competitor' : 'Channel'} was successfully ${
              !!channel ? 'updated' : 'added'
            }`,
          );
        } catch (error) {
          handleFormErrors<FormDataT>(error, setError, alerts);
        }
      },
    )(e);
  };
  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <>
      <Modal>
        <SidePanel
          heading={`${!!channel ? 'Edit' : 'Add'} ${
            competitor ? 'competitor' : 'channel'
          }`}
          onClose={onClose}
        >
          <SidePanelBody onSubmit={onSubmit} autoComplete="off">
            <SidePanelContent>
              <FormLayout>
                {!competitor && (
                  <Larger>
                    <Note type="question" title="Channel creation guide">
                      Before adding new channel to the list, please check
                      channel creation guide and how it should be correctly
                      created.
                      <br />
                      <ReadMore
                        onClick={() => setShowCreationGuide((prev) => !prev)}
                      >
                        Read more
                      </ReadMore>
                    </Note>
                  </Larger>
                )}
                {!channel && (
                  <RadioBox>
                    <RadiosLabel>
                      Channel Link
                      <RadioInput
                        type="radio"
                        name={`channel-link-id-radio`}
                        checked={activeChannelInput === 'link'}
                        onChange={() => setActiveChannelInput('link')}
                      />
                      <RadioCircle />
                    </RadiosLabel>
                    <RadiosLabel>
                      Channel ID
                      <RadioInput
                        type="radio"
                        name={`channel-link-id-radio`}
                        checked={activeChannelInput === 'id'}
                        onChange={() => setActiveChannelInput('id')}
                      />
                      <RadioCircle />
                    </RadiosLabel>
                  </RadioBox>
                )}
                {activeChannelInput === 'id' ? (
                  <Larger>
                    <Label htmlFor="idInput">Channel ID</Label>
                    <Input
                      key={activeChannelInput}
                      $hasError={!!errors.id}
                      id="idInput"
                      placeholder="Add channel ID"
                      {...register('id')}
                    />
                    {!!errors.id && (
                      <ValidationMessage>{errors.id.message}</ValidationMessage>
                    )}
                  </Larger>
                ) : (
                  <Larger>
                    <Label htmlFor="linkInput">Channel link</Label>
                    <Input
                      key={activeChannelInput}
                      $hasError={!!errors.link}
                      id="linkInput"
                      type="url"
                      placeholder="Add channel link"
                      disabled={!!channel}
                      {...register('link')}
                    />
                    {!!errors.link && (
                      <ValidationMessage>
                        {errors.link.message}
                      </ValidationMessage>
                    )}
                  </Larger>
                )}
                {/* TODO: needs cleanup */}
                {/* <Larger>
                  <Checkbox control={control} name="dedicatedForTesting">
                    Dedicated for testing
                  </Checkbox>
                </Larger> */}
                <Larger onClick={(e) => e.stopPropagation()}>
                  <Label htmlFor="networkSelect">Network</Label>
                  <Dropdown
                    $hasError={!!errors.network}
                    options={networks}
                    control={control}
                    disabled={!!channel}
                    name="network"
                    id="networkSelect"
                  />
                  {!!errors.network && (
                    <ValidationMessage>
                      {(errors.network as FieldError).message}
                    </ValidationMessage>
                  )}
                </Larger>
                <ChannelTagsField
                  control={control}
                  setValue={setValue}
                  error={errors.tags as unknown as FieldError}
                />
                <Larger>
                  <Label htmlFor="notesTextarea">Notes</Label>
                  <Textarea
                    id="notesTextarea"
                    placeholder="Add notes"
                    $hasError={!!errors.notes}
                    {...register('notes')}
                  />
                  {!!errors.notes && (
                    <ValidationMessage>
                      {errors.notes.message}
                    </ValidationMessage>
                  )}
                </Larger>
              </FormLayout>
            </SidePanelContent>
            <SidePanelFooter $actions>
              <Button disabled={isSubmitting} loading={isSubmitting}>
                {!!channel ? 'Update' : 'Add'}
              </Button>
              <Button color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </SidePanelFooter>
          </SidePanelBody>
        </SidePanel>
      </Modal>
      <AnimatePresence>
        {showCreationGuide && (
          <ChannelCreationGuide onClose={() => setShowCreationGuide(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ChannelFormModal;
